import { useState, useContext, useMemo } from "react";

import {
  Button,
  CircularProgress,
  Typography,
  useTheme,
  Box,
  Divider,
  Stack,
  Grid,
} from "@mui/material";

import { Alert } from "@mui/material";
import { AppContext } from "../contextProviders/AppContext";
import ResultTimeSelector from "./ResultTimeSelector";
import { centsToLocalString } from "../shared/money";
import { FormattedDate, FormattedTime } from "./common";
import AdditionalFieldsDialog from "./AdditionalFieldsDialog";
import {
  differenceInMinutes,
  intervalToDuration,
  addMinutes,
  formatDuration,
} from "date-fns";
import { fi } from "date-fns/locale";
import { hideZeroPrices } from "../features";
import { ADDITIONAL_FIELDS_ALL } from "../shared/additional-fields";
import { translation } from "./Dictionary";
import React from "react";
import ProductImage from "./ProductImage";
import { getFieldValue, getFixedLanguageSet } from "../utilities";

export default function ProductCard({
  initialOffer,
  FEATURE_HIDE_IF_NOT_TIMEMATCHES,
}) {
  const {
    companies,
    FEATURE_FIXED_RESERVATION_DURATION,
    mobileViewPort,
    reservationStart,
    reservationReturn,
    selectedCompany,
  } = useContext(AppContext);

  const theme = useTheme();
  const [offer, setOffer] = useState(initialOffer);

  const [priceLoading, setPriceLoading] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  const [rescheduleOfferStart, setRescheduleOfferStart] = useState(
    initialOffer.startTime
  );
  const [rescheduleOfferReturn, setRescheduleOfferReturn] = useState(
    initialOffer.returnTime
  );

  const isOfferAvailable = useMemo(() => {
    return (
      initialOffer.startTime.getTime() === reservationStart.getTime() &&
      initialOffer.returnTime.getTime() === reservationReturn.getTime()
    );
  }, [
    initialOffer.startTime,
    initialOffer.returnTime,
    reservationStart,
    reservationReturn,
  ]);

  // This company has hard coded stuff that doesnt support ResultTimeSelector
  if (FEATURE_HIDE_IF_NOT_TIMEMATCHES && !isOfferAvailable) {
    return null;
  }

  return (
    <>
      <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          marginY: "10px",
          // border: "1px solid lightgrey",
          justifyContent: "center",
          bgcolor: theme.palette.secondary.background,
        }}
        spacing={1}
      >
        <ProductTitle
          offer={initialOffer}
          selectedCompany={selectedCompany}
          companies={companies}
          FEATURE_FIXED_RESERVATION_DURATION={
            FEATURE_FIXED_RESERVATION_DURATION
          }
        />
        <SuggestedReservationScheduleAlert
          offer={initialOffer}
          isOfferAvailable={isOfferAvailable}
        />
        <RescheduleReservation
          offer={offer}
          setOffer={setOffer}
          setPriceLoading={setPriceLoading}
          isOfferAvailable={isOfferAvailable}
          rescheduleOfferReturn={rescheduleOfferReturn}
          setRescheduleOfferReturn={setRescheduleOfferReturn}
          rescheduleOfferStart={rescheduleOfferStart}
          setRescheduleOfferStart={setRescheduleOfferStart}
        />
        <Stack
          direction={mobileViewPort ? "column" : "row"}
          spacing={1}
          flex={1}
          justifyContent={"space-between"}
        >
          <Stack
            direction={mobileViewPort ? "column" : "row"}
            alignItems={mobileViewPort ? undefined : "center"}
            flex={1}
          >
            <Box
              sx={{
                padding: "10px",
                width: { md: "35%", sm: "100%" },
                height: "240px",
                overflow: "hidden",
              }}
            >
              <ProductImage offer={initialOffer} />
            </Box>
            <Stack flex={1} spacing={1}>
              <ProductAttributes offer={initialOffer} />
              <ShowProductSpecifications
                setShowAdditionalFields={setShowAdditionalFields}
                offer={initialOffer}
              />
            </Stack>
          </Stack>

          <ReserveProduct
            offer={offer}
            priceLoading={priceLoading}
            isOfferAvailable={isOfferAvailable}
            rescheduleOfferReturn={rescheduleOfferReturn}
            rescheduleOfferStart={rescheduleOfferStart}
          />
        </Stack>
      </Stack>
      {showAdditionalFields && (
        <AdditionalFieldsDialog
          offer={initialOffer}
          theme={theme}
          onClose={() => setShowAdditionalFields(false)}
        />
      )}
    </>
  );
}

const getPrimaryAttributes = (additionalFields, selectedLanguage) => {
  /*   return additionalFields.filter(
    (f) => ADDITIONAL_FIELDS_ALL.find((af) => af.key === f.key)?.primaryFilter
  ); */
  return additionalFields.reduce((acc, field) => {
    const fieldData = ADDITIONAL_FIELDS_ALL.find((f) => f.key === field.key);
    if (fieldData?.primaryFilter) {
      if (field?.value || field?.valueT?.[selectedLanguage]) acc.push(field);
    }
    return acc;
  }, []);
};

function ProductAttributes(props) {
  const { mobileViewPort, language } = useContext(AppContext);
  const { offer } = props;
  const additionalFields = getPrimaryAttributes(
    offer.vehicle?.additionalFields,
    language
  );

  return (
    <Grid
      container
      direction={"row"}
      flex={1}
      spacing={1}
      sx={{ paddingX: "10px" }}
    >
      {additionalFields.map((item) => {
        return (
          <Grid item md={3} key={item.key}>
            <ProductAttribute item={item} />
          </Grid>
        );
      })}
    </Grid>
  );
}

function ShowProductSpecifications({ offer, setShowAdditionalFields }) {
  const { mobileViewPort, language } = useContext(AppContext);
  const theme = useTheme();
  return (
    <Box
      onClick={() => setShowAdditionalFields(true)}
      sx={{
        display: "flex",
        cursor: "pointer",
        color: theme.palette.primary.additionalInfo,
        textDecoration: "underline",
        flex: 1,
        paddingLeft: "10px",
      }}
    >
      <Typography
        variant="body1"
        align={mobileViewPort ? "center" : "left"}
        style={{
          color: theme.palette.primary.additionalInfo,
        }}
      >
        {translation.get(language, "More information")}
      </Typography>
    </Box>
  );
}

function ProductAttribute({ item }) {
  const { language } = useContext(AppContext);
  const fixedLanguage = getFixedLanguageSet(language);
  const theme = useTheme();
  const fieldData = ADDITIONAL_FIELDS_ALL.find((f) => f.key === item.key);

  const fieldValue = getFieldValue(item, fixedLanguage);

  return (
    <Stack direction={"row"} spacing={1} flex={1}>
      <Box sx={{ fontSize: "20px", color: theme.palette.primary.extraFields }}>
        {fieldData?.icon}
      </Box>
      <Typography
        variant="caption"
        color={theme.palette.primary.mainText}
        style={{
          marginLeft: 5,
          fontSize: 13,
          color: theme.palette.primary.text,
          fontWeight: 600,
        }}
        noWrap
      >
        {fieldValue} {fieldData?.unitT?.[fixedLanguage]}
      </Typography>
    </Stack>
  );
}

export function RescheduledTime({ date }) {
  return (
    <>
      <FormattedDate date={date} /> <FormattedTime date={date} />
    </>
  );
}

function RescheduleReservation({
  offer,
  setOffer,
  setPriceLoading,
  isOfferAvailable,
  rescheduleOfferReturn,
  setRescheduleOfferReturn,
  rescheduleOfferStart,
  setRescheduleOfferStart,
}) {
  const { reservationStart, reservationReturn, selectedCompany } =
    useContext(AppContext);

  if (!isOfferAvailable)
    return (
      <ResultTimeSelector
        freeTimes={offer.freeTimes}
        startTime={rescheduleOfferStart}
        returnTime={rescheduleOfferReturn}
        requestedStartTime={reservationStart}
        suggestedStartTime={offer.startTime}
        suggestedReturnTime={offer.returnTime}
        useVehicleTimes={offer.useVehicleTimes}
        company={selectedCompany}
        disableOffHourReturn={selectedCompany?.disableOffHourReturn}
        vehicle={offer.vehicle}
        setRescheduleOfferReturn={setRescheduleOfferReturn}
        setRescheduleOfferStart={setRescheduleOfferStart}
        offer={offer}
        setOffer={setOffer}
        rescheduleOfferReturn={rescheduleOfferReturn}
        rescheduleOfferStart={rescheduleOfferStart}
        setPriceLoading={setPriceLoading}
      />
    );
}

function SuggestedReservationScheduleAlert({ offer, isOfferAvailable }) {
  const { language, reservationStart, reservationReturn } =
    useContext(AppContext);
  const theme = useTheme();

  const diffString = useMemo(() => {
    const diff = differenceInMinutes(offer.returnTime, offer.startTime);

    const duration = intervalToDuration({
      start: new Date(),
      end: addMinutes(new Date(), diff),
    });
    const formattedStr = formatDuration(duration, {
      format: ["days", "hours", "minutes"],
      locale: fi,
    });
    return formattedStr;
  }, [offer]);

  if (!isOfferAvailable)
    return (
      <Alert
        severity="info"
        sx={{
          backgroundColor: theme.palette.primary.rescheduleAlert,
          color: theme.palette.primary.mainText,
          marginBottom: "3px",
          borderRadius: "14px",
        }}
      >
        {translation.get(language, "No time match")}{" "}
        <strong>{offer.useVehicleTimes ? "" : diffString}</strong>{" "}
        {translation.get(language, "For reservation")}
        <strong>
          <RescheduledTime
            date={offer.startTime}
            requestedDate={reservationStart}
          />
        </strong>
      </Alert>
    );
}

function ProductTitle(props) {
  const {
    offer,
    selectedCompany,
    companies,
    FEATURE_FIXED_RESERVATION_DURATION,
  } = props;
  const { mobileViewPort } = useContext(AppContext);
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Typography
        align="left"
        sx={{
          fontFamily: "Sofia Pro",
          fontSize: mobileViewPort ? 24 : 26,
          color: theme.palette.primary.mainText,
          marginLeft: "5px",
        }}
        color={theme.palette.primary.mainText}
      >
        {offer.vehicle.name}
      </Typography>
      {selectedCompany.id === "all" && (
        <Typography
          style={{ fontSize: "18px", marginLeft: "5px" }}
          variant="caption"
          color={theme.palette.primary.mainText}
        >
          {companies.find((c) => c.id === offer.vehicle.companyId)?.name ?? ""}
        </Typography>
      )}
      <Divider />
      <ProductOrEquilevantText offer={offer} />
    </Box>
  );
}

function ProductOrEquilevantText({ offer }) {
  const { language, FEATURE_FIXED_RESERVATION_DURATION } =
    useContext(AppContext);
  const theme = useTheme();
  if (offer.availableInCategory && !FEATURE_FIXED_RESERVATION_DURATION) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
        }}
      >
        <Typography
          color={theme.palette.primary.mainText}
          sx={{
            fontSize: "12px",
            color: theme.palette.primary.mainText,
            marginLeft: "10px",
          }}
          variant="body1"
        >
          {translation.get(language, "Or equivalent text")}{" "}
          {offer.vehicle.category.name}{" "}
          {translation.get(language, "product text")}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.primary.mainText,
            marginLeft: "10px",
          }}
          variant="body1"
        >
          {offer.availableInCategory}{" "}
          {offer.availableInCategory > 1 && language === "fi"
            ? "kappaletta"
            : offer.availableInCategory === 1 && language === "fi"
            ? "kappale "
            : translation.get(language, "pcs text")}{" "}
          {translation.get(language, "Available text")}
        </Typography>
      </Box>
    );
  }
}

function ReserveProduct(props) {
  const {
    offer,
    priceLoading,
    isOfferAvailable,
    rescheduleOfferReturn,
    rescheduleOfferStart,
  } = props;
  const {
    mobileViewPort,
    language,
    selectedCompany,
    handleActiveStep,
    activeStep,
    setOffers,
    cart,
    enableCart,
    addToCart,
    setReservationReturn,
    setReservationStart,
    deleteProduct,
  } = useContext(AppContext);
  const theme = useTheme();

  const offerReserved = cart?.find(
    (item) => item.vehicle.id === offer.vehicle.id
  );

  const handleAdvance = () => {
    setOffers([]);
    handleActiveStep(activeStep + 1);
  };

  const FEATURE_HIDE_ZERO_PRICES = useMemo(() => {
    return hideZeroPrices(process.env.REACT_APP_ENV, selectedCompany);
  }, [selectedCompany]);

  const productQuantity = (id) => {
    const isItemInCart = cart.find((item) => item.vehicle.id === id);
    if (!isItemInCart) return 0;
    return isItemInCart.quantity;
  };

  const reduceQuantity = () => {
    if (productQuantity(offer.vehicle.id) <= 1) return deleteProduct(offer);
    let productWithQuantity = {
      ...offer,
      quantity: productQuantity(offer.vehicle.id) - 1,
      productPrice: offerPrice,
      offerPrice: calculateOfferPrice(
        offerPrice,
        productQuantity(offer.vehicle.id) - 1
      ),
      additionalServices: offer.additionalServiceOffers,
      availableInCategory: offer.availableInCategory,
      vehicle: offer.vehicle,
    };
    addToCart(productWithQuantity);
  };
  const addQuantity = () => {
    if (offer.availableInCategory) {
      if (productQuantity(offer.vehicle.id) === offer.availableInCategory)
        return;
    }
    let productWithQuantity = {
      ...offer,
      quantity: productQuantity(offer.vehicle.id) + 1,
      productPrice: offerPrice,
      offerPrice: calculateOfferPrice(
        offerPrice,
        productQuantity(offer.vehicle.id) + 1
      ),
      additionalServices: offer.additionalServiceOffers,
      availableInCategory: offer.availableInCategory,
      vehicle: offer.vehicle,
    };
    addToCart(productWithQuantity);
  };

  const reserveProduct = (offer) => {
    // offer is selected and placed in the cart, button has no actions
    if (offerReserved) return;

    // add quantity for the selected product
    let productWithQuantity = {
      quantity: 1,
      productPrice: offerPrice,
      offerPrice: calculateOfferPrice(offerPrice, 1),
      tax: offer.tax,
      additionalServices: offer.additionalServiceOffers,
      availableInCategory: offer.availableInCategory,
      vehicle: offer.vehicle,
    };

    // proceed when new timeframe is selected from reservation calendar
    if (!isOfferAvailable) {
      setReservationReturn(rescheduleOfferReturn);
      setReservationStart(rescheduleOfferStart);
      handleActiveStep(activeStep + 1);
      addToCart(productWithQuantity);

      addToCart(productWithQuantity);
      handleActiveStep(activeStep + 1);
      // when enableCart, stay at the current step and calmly but firmly, place the product in to the cart
    } else {
    }
    if (enableCart && isOfferAvailable) {
      addToCart(productWithQuantity);
      // no enableCart, so grab what you can and rush to the next step
    } else {
      addToCart(productWithQuantity);
      handleActiveStep(activeStep + 1);
    }
  };

  const offerPrice = offer.vehicleOfferPrice;
  return (
    <Stack justifyContent={"center"} spacing={1}>
      <Box
        sx={{
          marginY:
            offer.availableInCategory > 1 && offerReserved ? undefined : "10px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <Typography
          align="left"
          sx={{
            fontFamily: "Sofia Pro",
            fontSize: mobileViewPort ? 20 : 24,
            fontWeight: 700,
            color: theme.palette.primary.mainText,
          }}
        >
          {priceLoading ? (
            <CircularProgress size="1em" />
          ) : FEATURE_HIDE_ZERO_PRICES && offerPrice === 0 ? (
            ""
          ) : (
            `${centsToLocalString(offerPrice)} €`
          )}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          width: "100%",
        }}
      >
        {offer.availableInCategory > 1 && offerReserved && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "50%",
              alignItems: "flex-end",
              justifyContent: "center",
              height: "100%",
              marginX: "10px",
            }}
          >
            <Typography sx={{ marginBottom: "2px", marginRight: "7px" }}>
              {translation.get(language, "Quantity text")}
            </Typography>

            <div
              style={{
                display: "flex",
                marginBottom: "21px",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                disableElevation
                onClick={() => reduceQuantity(offer)}
                sx={{
                  color: theme.palette.primary.buttonText,
                  backgroundColor: theme.palette.primary.decButton,
                  maxWidth: "35px",
                  maxHeight: "35px",
                  minWidth: "35px",
                  minHeight: "35px",
                  fontWeight: "bold",
                  borderBottomRightRadius: 0,
                  borderTopRightRadius: 0,
                }}
              >
                <Typography variant="body2">-</Typography>
              </Button>

              <div
                style={{
                  backgroundColor: theme.palette.primary.qtyBackground,
                  display: "flex",

                  alignItems: "center",
                  justifyContent: "center",
                  maxWidth: "35px",
                  maxHeight: "35px",
                  minWidth: "35px",
                  minHeight: "35px",
                  fontWeight: "bolder",
                }}
              >
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    color: theme.palette.primary.mainText,
                  }}
                >
                  {getProductQuantity(offer.vehicle.id, cart)}
                </Typography>
              </div>
              <Button
                variant="contained"
                disableElevation
                onClick={() => addQuantity(offer)}
                style={{
                  color: theme.palette.primary.buttonText,
                  backgroundColor: theme.palette.primary.addButton,
                  maxWidth: "35px",
                  maxHeight: "35px",
                  minWidth: "35px",
                  minHeight: "35px",
                  fontWeight: "bold",
                  borderBottomLeftRadius: 0,
                  borderTopLeftRadius: 0,
                }}
              >
                <Typography variant="body2">+</Typography>
              </Button>
            </div>
          </Box>
        )}
        {!offerReserved && (
          <Button
            sx={{
              textTransform: "none",
              letterSpacing: "1px",
              width: "175px",
              paddingY: "12px",

              bgcolor: offerReserved
                ? theme.palette.secondary.reservedButtonColor
                : theme.palette.primary.reservedButtonColor,
            }}
            variant="contained"
            onClick={() => reserveProduct(offer)}
            disabled={!isOfferAvailable && cart.length >= 1}
          >
            <Typography sx={{ fontSize: "16px", fontFamily: "Sofia Pro" }}>
              {offerReserved
                ? translation.get(language, "Reserved text")
                : translation.get(language, "Reserve product")}
            </Typography>
          </Button>
        )}
        {cart.length >= 1 && offerReserved && (
          <Button
            sx={{
              textTransform: "none",
              letterSpacing: "1px",
              minWidth: "175px",
              paddingY: "12px",
              bgcolor: offerReserved
                ? theme.palette.secondary.reservedButtonColor
                : theme.palette.primary.reservedButtonColor,
            }}
            variant="contained"
            onClick={() => handleAdvance()}
            disabled={!isOfferAvailable && cart.length >= 1}
          >
            <Typography sx={{ fontSize: "16px", fontFamily: "Sofia Pro" }}>
              {translation.get(language, "Continue order")}
            </Typography>
          </Button>
        )}
      </Box>
    </Stack>
  );
}

const calculateOfferPrice = (value, quantity) => {
  return value * quantity;
};

const getProductQuantity = (productId, cart) => {
  const isItemInCart = cart.find((item) => item.vehicle.id === productId);
  if (!isItemInCart) return 0;
  return isItemInCart.quantity;
};
